import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
// import LiquidEther from './LiquidEther'; // Lazy load this
import TextType from './TextType';
import { ChevronDown } from 'lucide-react';

const SpaceBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = window.innerWidth;
    let height = window.innerHeight;
    canvas.width = width;
    canvas.height = height;

    const stars: { x: number; y: number; s: number; alpha: number }[] = [];
    for (let i = 0; i < 200; i++) {
      stars.push({
        x: Math.random() * width,
        y: Math.random() * height,
        s: Math.random() * 2,
        alpha: Math.random()
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = "white";
      stars.forEach(star => {
        ctx.globalAlpha = star.alpha;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.s, 0, 2 * Math.PI);
        ctx.fill();
        // Drift
        star.y -= star.s * 0.2;
        if (star.y < 0) star.y = height;
      });
      requestAnimationFrame(draw);
    };
    draw();

    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return <canvas ref={canvasRef} className="w-full h-full object-cover opacity-50" />;
};

const LiquidEther = React.lazy(() => import('./LiquidEther'));

const Hero: React.FC = () => {
  const containerRef = useRef<HTMLElement>(null);

  // Track scroll progress of the tall container
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  // Smooth out the scroll progress
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 20,
    restDelta: 0.001
  });

  // --- Scroll Triggers Mapping --- //

  // 1. Initial State: "Scroll to Launch" (0% - 10%)
  const introOpacity = useTransform(smoothProgress, [0, 0.08], [1, 0]);
  const introScale = useTransform(smoothProgress, [0, 0.08], [1, 0.8]);
  const introY = useTransform(smoothProgress, [0, 0.08], [0, -50]);

  // 2. Line 1: "From Static Pages" (Left to Center) (15% - 30%)
  const line1Opacity = useTransform(smoothProgress, [0.12, 0.25], [0, 1]);
  const line1X = useTransform(smoothProgress, [0.12, 0.25], ["-50vw", "0vw"]);

  // 3. Line 2: "to Smart Automation" (Right to Center) (30% - 45%)
  const line2Opacity = useTransform(smoothProgress, [0.30, 0.45], [0, 1]);
  const line2X = useTransform(smoothProgress, [0.30, 0.45], ["50vw", "0vw"]);

  // 4. Line 3: "Web Development..." (Bottom to Center) (45% - 60%)
  const line3Opacity = useTransform(smoothProgress, [0.45, 0.60], [0, 1]);
  const line3Y = useTransform(smoothProgress, [0.45, 0.60], ["100px", "0px"]);

  // 5. Exit Animation (Parallax Fade Out) 
  // Adjusted to [0.75, 1.0] to fade out while AboutSection overlaps
  const contentExitScale = useTransform(smoothProgress, [0.75, 1.0], [1, 0.8]);
  const contentExitOpacity = useTransform(smoothProgress, [0.75, 0.95], [1, 0]);
  const contentExitBlur = useTransform(smoothProgress, [0.75, 0.95], ["blur(0px)", "blur(10px)"]);

  return (
    <section
      ref={containerRef}
      className="relative w-full h-[350vh] bg-black z-0" // Extra height for scroll timeline
    >
      {/* Sticky Container: Holds the view in place while scrolling through the section */}
      <div className="sticky top-0 w-full h-screen overflow-hidden flex flex-col items-center justify-center">

        {/* Background Layers (Consistent across scroll) */}
        <div className="absolute inset-0 z-0">
          <SpaceBackground />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-black/90" />
        </div>

        <motion.div
          style={{ opacity: contentExitOpacity }}
          className="absolute inset-0 z-0 mix-blend-screen opacity-60 pointer-events-none"
        >
          <React.Suspense fallback={null}>
            <LiquidEther
              colors={['#cbd5e1', '#ffffff', '#ffedd5']}
              mouseForce={15}
              cursorSize={60}
              isViscous={false}
              viscous={20}
              iterationsViscous={10}
              iterationsPoisson={10}
              resolution={0.35}
              isBounce={false}
              autoDemo={true}
              autoSpeed={0.1}
              autoIntensity={2.0}
              takeoverDuration={0.25}
              autoResumeDelay={3000}
              autoRampDuration={0.6}
            />
          </React.Suspense>
        </motion.div>

        {/* --- Phase 1: Initial Prompt --- */}
        <motion.div
          style={{ opacity: introOpacity, scale: introScale, y: introY }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }} // Fade in after loading
          className="absolute z-20 flex flex-col items-center gap-4"
        >
          <div className="bg-white/5 backdrop-blur-md border border-white/10 px-8 py-4 rounded-full shadow-[0_0_30px_rgba(255,255,255,0.05)]">
            <TextType
              text="Scroll to launch"
              typingSpeed={100}
              startOnVisible={true}
              initialDelay={1000} // Increased delay to wait for menu animation
              showCursor={true}
              cursorCharacter="|"
              className="text-lg md:text-xl font-mono text-orange-100 uppercase tracking-widest"
            />
          </div>
          <motion.div
            animate={{ y: [0, 10, 0], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity, delay: 2.5 }}
          >
            <ChevronDown className="text-orange-200/70" size={32} />
          </motion.div>
        </motion.div>


        {/* --- Main Content Container --- */}
        <motion.div
          style={{
            scale: contentExitScale,
            opacity: contentExitOpacity,
            filter: contentExitBlur
          }}
          className="relative z-10 flex flex-col items-center text-center w-full max-w-7xl px-4"
        >

          {/* Line 1 */}
          <motion.div
            style={{ x: line1X, opacity: line1Opacity }}
            className="w-full"
          >
            <motion.h1
              animate={{ y: [0, -10, 0], rotate: [0, 1, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="text-4xl md:text-5xl lg:text-7xl font-bold tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white to-slate-400 drop-shadow-2xl mb-2 md:mb-4 inline-block pb-2"
            >
              From Static Pages
            </motion.h1>
          </motion.div>

          {/* Line 2 */}
          <motion.div
            style={{ x: line2X, opacity: line2Opacity }}
            className="w-full"
          >
            <motion.h1
              animate={{ y: [0, 10, 0], rotate: [0, -1, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              className="text-4xl md:text-5xl lg:text-7xl font-bold tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-slate-200 to-slate-600 drop-shadow-2xl inline-block pb-2"
            >
              to Smart Automation
            </motion.h1>
          </motion.div>

          {/* Line 3 (Description) */}
          <motion.div
            style={{ y: line3Y, opacity: line3Opacity }}
            className="mt-12 md:mt-16 max-w-3xl mx-auto"
          >
            <motion.div
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="backdrop-blur-md bg-white/5 p-6 rounded-2xl border border-white/10 shadow-[0_0_30px_-10px_rgba(255,255,255,0.1)] relative overflow-hidden group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <p className="text-xl md:text-2xl text-orange-50/90 font-light tracking-wide leading-relaxed relative z-10">
                Web Development and Workflow Automation
              </p>
            </motion.div>
          </motion.div>

        </motion.div>

        {/* Overlay Vignette */}
        <div className="absolute inset-0 pointer-events-none bg-radial-gradient from-transparent via-transparent to-black/90 z-0" />
      </div>
    </section>
  );
};

export default Hero;